import { computeVideoDiff, type VideoStateSnapshot } from '../video-state';
import type {
    AssetDescription,
    ComparedSceneGroupKey,
    ComparedSceneKey,
    TemplateDescription,
    VideoDescription,
    VideoDiff,
} from '../video-state/types';

import type { ChangeKind, SceneChangeKind } from './changeKinds';

import { ErrorReporter } from 'js/src/libs/error-reporter';

/** What changed between two video states, in the terms lint checks declare their causes in. */
export type VideoChanges = {
    /** A build from an empty video: every scene is new, and every finding is the agent's. */
    isBuild: boolean;
    /** How each scene changed, by scene id. A scene that is new holds 'added'. */
    scenes: ReadonlyMap<string, ReadonlySet<ChangeKind | 'added'>>;
    music: boolean;
    sceneList: boolean;
};

type Kinds = readonly SceneChangeKind[];

/** Every way a scene can look different, for a change nothing narrower describes. */
export const ANY_VISUAL_CHANGE: Kinds = ['text', 'layout', 'colour', 'media'];

const GROUP_TIMING: Kinds = ['group_timing'];

/*
 * What a change to each field of the video state is. Every table is exhaustive over its type, so a
 * field added to the video state has to say what it is before this compiles. A field the state
 * carries without its type saying so is reported, and counted as any visual change (see `kindsOf`).
 */

const SCENE_FIELD_KINDS: Record<ComparedSceneKey, Kinds> = {
    design: ['layout', 'colour', 'media'],
    sceneCategory: ['layout', 'colour', 'media'],
    subscenes: ['layout'],
    textPositionPresets: ['layout'],
    // How the scene is entered. A transition or match cut eats into how long its text is readable,
    // and the scene before it's too (see `whatChanged`).
    transitionIn: ['timing'],
    duration: ['timing'],
    coverage: ['timing'],
    captionsVisible: ['captions'],
};

type KeysOfUnion<T> = T extends unknown ? keyof T : never;

/** The asset fields a diff can report: its name identifies it, and `changedFromDesign` is skipped. */
type ComparedAssetKey = Exclude<KeysOfUnion<AssetDescription>, 'name' | 'changedFromDesign'>;

const ASSET_FIELD_KINDS: Record<ComparedAssetKey, Kinds> = {
    type: ANY_VISUAL_CHANGE,
    text: ['text'],
    texts: ['text'],
    parts: ['text'],
    status: ['text'],
    chartData: ['text'],
    bounds: ['layout'],
    subscene: ['layout'],
    lines: ['layout'],
    alignment: ['layout'],
    font: ['layout'],
    fontSize: ['layout'],
    sceneDesignId: ['layout'],
    children: ['layout'],
    childrenAlignment: ['layout'],
    position: ['layout'],
    chartType: ['layout'],
    colors: ['colour'],
    color: ['colour'],
    lineColor: ['colour'],
    iconColors: ['colour'],
    textColor: ['colour'],
    spokenWordHighlightColor: ['colour'],
    opacity: ['colour'],
    mediaId: ['media'],
    description: ['media'],
    mediaType: ['media'],
    mediaScale: ['media'],
    mediaResolution: ['media'],
    mediaTranslation: ['media'],
    mediaXTranslationRange: ['media'],
    mediaYTranslationRange: ['media'],
    linkedToSceneGroup: ['media'],
    framing: ['media'],
    rotation: ['media'],
    flipped: ['media'],
    brightness: ['media'],
    effects: ['media'],
    appearsAt: ['timing'],
    animated: ['timing'],
    trimStartMs: ['timing'],
    trimEndMs: ['timing'],
    sourceDurationMs: ['timing'],
    playbackMode: ['timing'],
    playbackRate: ['timing'],
};

/** A gradient's stops are keyed `colorStops.N.color`, so they're matched by prefix. */
const GRADIENT_STOP_PREFIX = 'colorStops.';

/**
 * What a change to a scene group is, for each of its scenes. Its narration and cuts retime them all;
 * its index and scene indices shift whenever a scene before it is added, and volumes are only heard.
 */
const SCENE_GROUP_FIELD_KINDS: Record<ComparedSceneGroupKey, Kinds> = {
    type: GROUP_TIMING,
    duration: GROUP_TIMING,
    transcript: GROUP_TIMING,
    voice: GROUP_TIMING,
    voiceSpeed: GROUP_TIMING,
    fillerWords: GROUP_TIMING,
    sceneIndices: [],
    voiceVolume: [],
    musicVolume: [],
};

/** What a change to the video's settings is, for every scene. */
const VIDEO_FIELD_KINDS: Record<keyof VideoDescription, Kinds> = {
    resolution: ['layout'],
    aspectRatio: ['layout'],
    colorGrading: ['media'],
    designSystem: ANY_VISUAL_CHANGE,
    // These follow from the scenes rather than restyle them.
    sceneCount: [],
    duration: [],
};

/** What a change to the template is, for every scene. */
const TEMPLATE_FIELD_KINDS: Record<keyof TemplateDescription, Kinds> = {
    template: ANY_VISUAL_CHANGE,
    templateFonts: ['layout'],
    templateColors: ['colour'],
    // Named presets on offer, not colours any scene uses.
    templatePalettes: [],
};

const reportedUnknownFields = new Set<string>();

/** Looks a field up in its table, reporting (once) one the table doesn't have. */
function kindsOf<K extends string>(
    table: Readonly<Record<K, Kinds>>,
    field: string,
    where: string,
): Kinds {
    if (Object.hasOwn(table, field)) {
        return table[field as K];
    }
    if (where === 'asset' && field.startsWith(GRADIENT_STOP_PREFIX)) {
        return ['colour'];
    }
    const key = `${where}.${field}`;
    if (!reportedUnknownFields.has(key)) {
        reportedUnknownFields.add(key);
        ErrorReporter.capture(
            new Error(
                `whatChanged has no change kind for the ${where} field "${field}"; counting it as any visual change`,
            ),
        );
    }
    return ANY_VISUAL_CHANGE;
}

function kindsOfFields<K extends string>(
    table: Readonly<Record<K, Kinds>>,
    changes: object | undefined,
    where: string,
): Kinds {
    return Object.keys(changes ?? {}).flatMap((field) => kindsOf(table, field, where));
}

/** The ids of each scene group's scenes, in order, keyed by the group's id. */
function scenesByGroup(state: VideoStateSnapshot): Map<string, string> {
    return new Map(
        state.sceneGroups.map((sceneGroup) => [
            sceneGroup.id ?? '',
            state.scenes
                .filter((scene) => scene.sceneGroupIndex === sceneGroup.index)
                .map((scene) => scene.id)
                .join(),
        ]),
    );
}

/**
 * How each scene group changed, for its scenes: its own fields, plus a retime when it gained, lost
 * or reordered a scene, or one of its scenes runs longer or shorter (`withResizedScenes`), which
 * shifts the scenes after it. Where a group sits in the video doesn't count: its timing checks are
 * measured within the group.
 */
function kindsBySceneGroup(
    before: VideoStateSnapshot,
    after: VideoStateSnapshot,
    diff: VideoDiff['changes'],
    withResizedScenes: ReadonlySet<string>,
): Map<string, Kinds> {
    const kinds = new Map<string, Kinds>();
    const add = (id: string, more: Kinds) => kinds.set(id, [...(kinds.get(id) ?? []), ...more]);
    for (const id of withResizedScenes) {
        add(id, GROUP_TIMING);
    }
    const scenesBefore = scenesByGroup(before);
    for (const [id, sceneIds] of scenesByGroup(after)) {
        if (scenesBefore.get(id) !== sceneIds) {
            add(id, GROUP_TIMING);
        }
    }
    for (const { id, changes } of diff.sceneGroups?.modified ?? []) {
        if (id !== undefined) {
            add(id, kindsOfFields(SCENE_GROUP_FIELD_KINDS, changes, 'scene group'));
        }
    }
    return kinds;
}

/** Reads what an edit changed off the video state before it and after it. */
export function whatChanged(before: VideoStateSnapshot, after: VideoStateSnapshot): VideoChanges {
    const diff = computeVideoDiff(before, after).changes;
    const scenes = new Map<string, Set<ChangeKind | 'added'>>();
    const addKinds = (sceneId: string | undefined, kinds: readonly (ChangeKind | 'added')[]) => {
        if (sceneId === undefined || kinds.length === 0) {
            return;
        }
        const sceneKinds = scenes.get(sceneId) ?? new Set();
        scenes.set(sceneId, sceneKinds);
        kinds.forEach((kind) => sceneKinds.add(kind));
    };

    for (const { id } of diff.scenes.added) {
        addKinds(id, ['added']);
    }
    const withResizedScenes = new Set<string>();
    for (const { id, sceneGroupIndex, changes } of diff.scenes.modified) {
        const { assets, ...fields } = changes;
        const sceneGroupId = after.sceneGroups[sceneGroupIndex]?.id;
        if (fields.duration && sceneGroupId !== undefined) {
            withResizedScenes.add(sceneGroupId);
        }
        addKinds(id, kindsOfFields(SCENE_FIELD_KINDS, fields, 'scene'));
        if (fields.transitionIn) {
            // The seam is shared: whatever now plays across it also overlaps the scene before.
            const position = after.scenes.findIndex((scene) => scene.id === id);
            addKinds(after.scenes[position - 1]?.id, ['timing']);
        }
        if (assets.added.length > 0 || assets.removed.length > 0) {
            addKinds(id, ANY_VISUAL_CHANGE);
        }
        for (const asset of assets.modified) {
            addKinds(id, kindsOfFields(ASSET_FIELD_KINDS, asset.changes, 'asset'));
        }
    }

    // Changes to the video as a whole, and to each scene group, reach every scene they cover.
    const forEveryScene = [
        ...kindsOfFields(VIDEO_FIELD_KINDS, diff.video, 'video'),
        ...kindsOfFields(TEMPLATE_FIELD_KINDS, diff.template, 'template'),
        ...(diff.captions ? (['captions'] as const) : []),
    ];
    const bySceneGroup = kindsBySceneGroup(before, after, diff, withResizedScenes);
    for (const scene of after.scenes) {
        const sceneGroupId = after.sceneGroups[scene.sceneGroupIndex]?.id;
        addKinds(scene.id, forEveryScene);
        addKinds(scene.id, (sceneGroupId && bySceneGroup.get(sceneGroupId)) || []);
    }

    return {
        isBuild: before.scenes.length === 0,
        scenes,
        music: diff.music !== undefined,
        sceneList: diff.scenes.added.length > 0 || diff.scenes.removed.length > 0,
    };
}
